import React, { useState } from 'react';
import { CheckCircle2, ChevronDown, ChevronUp, Circle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/Button';

interface AlertsQuickStartProps {
  rulesCount: number;
  hasUsableChannel: boolean;
  testCompleted: boolean;
  onGoToConnections: () => void;
  onAddRule: () => void;
  onTestChannel: () => void;
  onDismiss: () => void;
}

interface StepItemProps {
  done: boolean;
  label: string;
  cta: string;
  onClick: () => void;
  /** Per-item padding - applied here so each `<li>` is a DIRECT child of
   *  `<ol>` (HTML spec; wrapping breaks screen reader list semantics). */
  className?: string;
}

// Compact step row. Icon + label centered together via flex items-center;
// `leading-none` on the label removes line-box padding so the optical center
// of the text matches the geometric center of the icon (no perceived offset).
// Icon is 20px to match text-base/sm visual weight - smaller icons looked
// "above" the label text in user testing.
const StepItem: React.FC<StepItemProps> = ({ done, label, cta, onClick, className }) => (
  <li
    className={
      'flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3' +
      (className ? ' ' + className : '')
    }
  >
    <div className="flex flex-1 items-center gap-2.5">
      {done ? (
        <CheckCircle2 className="shrink-0 text-accent-bullish" size={20} aria-hidden="true" />
      ) : (
        <Circle className="shrink-0 text-text-muted" size={20} aria-hidden="true" />
      )}
      <span className="text-sm font-medium leading-none text-text-primary">{label}</span>
    </div>
    <Button
      variant={done ? 'secondary' : 'primary'}
      size="sm"
      className="min-h-[36px] sm:ml-auto"
      onClick={onClick}
      disabled={done}
    >
      {cta}
    </Button>
  </li>
);

const AlertsQuickStart: React.FC<AlertsQuickStartProps> = ({
  rulesCount,
  hasUsableChannel,
  testCompleted,
  onGoToConnections,
  onAddRule,
  onTestChannel,
  onDismiss,
}) => {
  const { t } = useTranslation();
  // Collapse the step list by default to keep the wizard tiny on laptops
  // (was consuming most of the viewport on 1280×720 / 1366×768). The
  // collapsed state shows only a one-line "X / 3 done" summary; the user
  // can expand to see all steps + CTAs.
  const [expanded, setExpanded] = useState(false);

  const doneCount = (hasUsableChannel ? 1 : 0) + (rulesCount > 0 ? 1 : 0) + (testCompleted ? 1 : 0);

  return (
    <section className="mx-3 rounded-xl bg-accent-primary/[0.06] px-3 py-2.5 md:mx-0">
      <header className="flex items-center gap-2">
        <button
          type="button"
          className="-mx-1 flex flex-1 items-center gap-2 rounded-md px-1 py-1 text-left transition-colors hover:bg-surface-container-high focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary"
          aria-expanded={expanded}
          aria-controls="alerts-quickstart-steps"
          onClick={() => setExpanded((v) => !v)}
        >
          <h3 className="text-sm font-semibold text-text-primary">
            {t('alerts.quickStart.title')}
          </h3>
          <span className="text-xs text-text-muted">({doneCount}/3)</span>
          {expanded ? (
            <ChevronUp className="ml-auto text-text-muted" size={16} aria-hidden="true" />
          ) : (
            <ChevronDown className="ml-auto text-text-muted" size={16} aria-hidden="true" />
          )}
        </button>
        <button
          type="button"
          className="inline-flex min-h-[36px] min-w-[36px] items-center justify-center rounded-md text-text-muted transition-colors hover:bg-surface-container-high hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary"
          aria-label={t('alerts.quickStart.dismiss')}
          onClick={onDismiss}
        >
          <X size={16} aria-hidden="true" />
        </button>
      </header>
      {expanded && (
        <ol
          id="alerts-quickstart-steps"
          className="mt-2 flex flex-col divide-y divide-border-subtle"
        >
          <StepItem
            className="pb-2"
            done={hasUsableChannel}
            label={t('alerts.quickStart.step1.label')}
            cta={t('alerts.quickStart.step1.cta')}
            onClick={onGoToConnections}
          />
          <StepItem
            className="py-2"
            done={rulesCount > 0}
            label={t('alerts.quickStart.step2.label')}
            cta={t('alerts.quickStart.step2.cta')}
            onClick={onAddRule}
          />
          <StepItem
            className="pt-2"
            done={testCompleted}
            label={t('alerts.quickStart.step3.label')}
            cta={t('alerts.quickStart.step3.cta')}
            onClick={onTestChannel}
          />
        </ol>
      )}
    </section>
  );
};

export default AlertsQuickStart;
